import { WSEventType } from './enum/ws-event-type';
import { proxy } from 'comlink';
import { WsReconnectInfo } from './model/ws-reconnect-info';
import { WsResponse } from './model/ws-response';
import { WsReconnectResponse } from './model/ws-reconnect-response';

/**
 * This is a base class to implement websocket 
 */
export abstract class WebsocketClient {
  /**
   * This function used to make connection with websocket server
   * 
   * @param  websocketServer  - websocket url
   * @example
   * 
   * ```
   * connect('ws://localhost:8089');
   * ```
   */
  connect(websocketUrl: string, wsWorker?: any): void {
    const { connect } = wsWorker;
    connect(websocketUrl, proxy(this.checkWSEvent.bind(this)));
  }


  /**
 * @example
 * 
 * ```
 * attemptReconnect({reconnectInterval: 300, reconnectAttempts:4});
 * ```
 * @param  reconnectInfo - reconnectIntervalInMs in millisecond, maxReconnectAttempts in number and ws url
 *
 */
  public attemptReconnect(reconnectInfo: WsReconnectInfo, wsWorker?: any) {
    const { attemptReconnect } = wsWorker;
    attemptReconnect(reconnectInfo, proxy(this.checkWSEvent.bind(this)));
  }


  /**
   * Method to send to websocket worker
   * @param msg - message send to websocket
   * @example
   * ```
   * sendMessage('hi');
   * ```
   */
   sendMessage(msg: unknown, wsWorker: any): void {
    const { send } = wsWorker;
     send(msg);
  }


  /**
   * Callback method when a message received from websocket server
   *
   * @param msg -  message from server
   */
  protected abstract onMessage(msg: any): void;

  /**
   * Error callback method for websocket Subscription
   */
  protected abstract onError(): void;

  /**
   * Completed callback method for websocket Subscription
   */
  protected abstract onClosed(): void;

  /**
   * Callback method when a connection is ready to send and receive data
  */
  protected abstract onOpen(): void;

  /**
 * Callback method when a ws reconnect attempts in progress
*/
  protected abstract onReconnect(msg?: WsReconnectResponse): void;


  /**
   * Callback method when a ws reconnect attempts are completed
  */
  protected abstract onReconnectComplete(): void;

  /**
   * This method to perform the different actions on callback response
   * @param event - ws response 
   * @example
   * ```
   * checkWSEvent(event);
   * ```
   */
  checkWSEvent(wsResponse: WsResponse) {
    const message = wsResponse?.message ? wsResponse?.message : null;
    switch (wsResponse.type) {
      case WSEventType.ERROR: {
        this.onError();
        break;
      }
      case WSEventType.OPEN: {
        this.onOpen();
        break;
      }
      case WSEventType.MESSAGE: {
        this.onMessage(message);
        break;
      }
      case WSEventType.RECONNECT: {
        this.onReconnect(message as WsReconnectResponse);
        break;
      }
      case WSEventType.RECONNECT_COMPLETE: {
        this.onReconnectComplete();
        break;
      }
      case WSEventType.CLOSE: {
        this.onClosed();
        break;
      }
    }
  }
}